import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { XGlyph } from "../ui/icons";
import { LegalLinks } from "../ui/legal-links";
import { devIdentitiesQuery } from "../../lib/queries";
import { resetAndStartOver } from "../../lib/dev";
import { OnboardFace, OnboardShell, StepDots } from "./steps";

/**
 * STEP 1 OF THE RUN, CONNECT (PRD §2.2.3, FR-1.2 #1–#2; ruling C5).
 *
 * It is also the landing, and that is not two screens wearing one costume: the
 * product's front door and the first step of signing up are the same act, and
 * the reference build treats them as one. What changed under C5 is that it now
 * carries the step counter, so somebody deciding whether to start can see how
 * long this takes — two steps if they came to knock, three if they came for a
 * door of their own.
 *
 * IDENTITY COMES FROM X AND THERE IS NO FORM (FR-1.2 #1). No handle field, no
 * display name, no picture upload: the address is already theirs and nobody
 * picks it. The legal line sits under the button rather than in a modal
 * (§2.2.3).
 *
 * ONE LANE, AND IT IS X (ruling R-0826-1, owner 2026-08-26). There used to be an
 * email form under a divider here — a knock-only account that could never hang a
 * door. It is deleted, not hidden: a screen offering two ways in has to explain
 * why one of them is worse, and the answer was that it bought the person a
 * second-class account. So this screen is one button and the sentence beneath it.
 */

export const CONNECT_TITLE = "Give your inbox a Nob*";
export const CONNECT_LEAD =
  "Your anti‑spam AI agent. Anyone can pitch your agent. They cover the " +
  "agent’s vetting cost. You skip the irrelevant pitches.";
export const CONNECT_FOOTNOTE = "* Nob - A tiny, judgmental doorman for your giant inbox.";
export const CONNECT_LABEL = "Continue with X";
/**
 * THE CONSENT SENTENCE, AND IT NAMES BOTH INSTRUMENTS (ruling R-0919-2).
 *
 * It used to say only what X gets out of the arrangement. The two links under
 * it have been there since compliance gap #1, and a pair of links is not a
 * consent sentence: nothing on the screen said that pressing the button was
 * the act of accepting anything. The sign-up acceptance rows the server writes
 * carry the SHA-256 of these exact bytes, so this string is the server's —
 * `SIGN_IN_CONSENT` in `apps/api/src/legal/documents.ts`, which the api suite
 * reads this file to compare against, because the web bundle cannot import it.
 * Change one and the build fails until the other follows.
 */
export const CONNECT_LEGAL =
  "By continuing you accept Nob's terms of use and privacy policy. Other users can find " +
  "your Nob via your X account, and we never post on your behalf.";

type Props = {
  onContinueWithX: (as?: string) => void;
  error: string | null;
  /**
   * Two steps rather than three, because this visit started at somebody else's
   * door. Read from the held knock target by whoever renders this — the flag is
   * a fact about the visit, not about the screen.
   */
  knocker: boolean;
};

export function SignIn({ onContinueWithX, error, knocker }: Props) {
  /**
   * Only ever non-empty against the local stand-in provider. A product about
   * identity is impossible to try with one identity: you need a door owner and
   * a stranger, and switching between them should not require a console.
   */
  const { data: identities = [] } = useQuery(devIdentitiesQuery());
  /**
   * Two kinds of stand-in, and the difference is what may be done to them. The
   * play identities carry a local stack's accumulated history and are named in
   * the sentence below; the disposable ones exist to be deleted and get a row
   * with a Reset button. The server decides which is which (`disposable`) —
   * duplicating the `test_` rule here would put it in two places.
   */
  const play = identities.filter((id) => !id.disposable);
  const disposable = identities.filter((id) => id.disposable);
  /** Any handle at all: the stand-in mints one on the spot (FakeX `?as=`). */
  const [asHandle, setAsHandle] = useState("");
  const [resetting, setResetting] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  return (
    // About rides in the Terms · Privacy row at the foot of this screen, so
    // the header does not carry a second one (wave 88).
    <OnboardShell about={false}>
      <StepDots step={1} knocker={knocker} />
      <OnboardFace mood="watching" />

      <h1 className="nob-poster-title">{CONNECT_TITLE}</h1>
      <p className="nob-poster-lead">{CONNECT_LEAD}</p>
      {/* What the asterisk in the headline is for, on ONE line (owner,
          2026-09-25; wave 88). */}
      <p className="nob-poster-footnote nob-poster-tagline">{CONNECT_FOOTNOTE}</p>

      <div className="nob-poster-cta">
        <button type="button" onClick={() => onContinueWithX()} className="nob-poster-btn">
          <XGlyph />
          {CONNECT_LABEL}
        </button>
        {error && (
          <p className="nob-poster-error" role="alert">
            {error}
          </p>
        )}
      </div>

      {/* The legal line sits UNDER the button, not in a modal (§2.2.3), in
          at most three lines (owner, 2026-09-25; wave 88). */}
      <p className="nob-poster-legal nob-poster-consent">{CONNECT_LEGAL}</p>

      {identities.length > 0 && (
        <div className="mt-4 flex w-full flex-col items-center gap-2">
          <p className="nob-poster-hint">
            Development: X is a local stand-in. Sign in as{" "}
            {play.map((id, i) => (
              <span key={id.handle}>
                {i > 0 && " · "}
                <button
                  type="button"
                  onClick={() => onContinueWithX(id.handle)}
                  title={id.display_name}
                  className="nob-poster-inline font-mono"
                >
                  @{id.handle}
                </button>
              </span>
            ))}
            {" — or anyone else."}
          </p>
          {/*
            The disposable pair, and they get a row each rather than a place in
            the sentence above, because they carry a verb the play identities
            must never have: Reset hard-deletes the account so the next sign-in
            is a genuine first run. The tag is the one fact that decides which
            trial the money step will offer — 7-day Personal or 3-day Lite —
            and therefore which of the eight flows this row starts.
          */}
          {disposable.length > 0 && (
            <div className="w-full rounded border border-dashed border-fainter p-2">
              <p className="nob-poster-hint mb-1">
                Flow testing — these two reset. See docs/testing-flows.md.
              </p>
              {disposable.map((id) => (
                <div key={id.handle} className="flex items-center gap-2 py-0.5">
                  <span className="flex-1 truncate font-mono text-[13px]">@{id.handle}</span>
                  <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-fainter">
                    {id.premium ? "premium" : "regular"}
                  </span>
                  <button
                    type="button"
                    onClick={() => onContinueWithX(id.handle)}
                    className="nob-poster-inline"
                  >
                    Sign in
                  </button>
                  <button
                    type="button"
                    disabled={resetting !== null}
                    onClick={() => {
                      setResetting(id.handle);
                      setResetError(null);
                      void resetAndStartOver(id.handle).then((r) => {
                        // Success never lands here — the page is already
                        // navigating. A refusal is the server's sentence.
                        if (!r.ok) setResetError(r.message);
                        setResetting(null);
                      });
                    }}
                    className="nob-poster-inline"
                  >
                    {resetting === id.handle ? "Resetting…" : "Reset"}
                  </button>
                </div>
              ))}
              {resetError && (
                <p className="nob-poster-error" role="alert">
                  {resetError}
                </p>
              )}
            </div>
          )}
          {/* Two identities is not a product about identity. Type a handle and the
              stand-in mints that person: same handle, same account, every time. */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const handle = asHandle.trim().replace(/^@/, "");
              if (handle) onContinueWithX(handle);
            }}
            className="w-full"
          >
            <label htmlFor="dev-handle" className="sr-only">
              Sign in as any handle
            </label>
            <input
              id="dev-handle"
              type="text"
              autoComplete="off"
              spellCheck={false}
              placeholder="any handle"
              value={asHandle}
              onChange={(e) => setAsHandle(e.target.value)}
              className="nob-poster-input h-10 font-mono text-[13px]"
            />
          </form>
        </div>
      )}
      <LegalLinks className="mt-9" />
    </OnboardShell>
  );
}
